import * as Request from '@services/login';
import { getChannelInfo } from '@utils/channel-util';
import generatePath from '@utils/generatePath';
import { qsParse } from '@utils/url-utils';
import Button from 'ac-components/ac-btn';
import Input from 'ac-components/ac-ipt';
import Alert from 'ac-components/alert';
import classNames from 'classnames';
import { AGREEMENT_POLICY, AGREEMENT_USER } from 'constants/url';
import { useTranslation } from 'hooks/use-translation';
import { getState } from 'jumpstate';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Token } from 'states/interface';
import AreaSelect, { Area } from '../area-select';
import { View } from './interface';
import styles from './styles.scss';
import * as Trace from './trace';
import {
	AlertTip,
	CardTitle,
	CardWrap,
	CardFormItem,
	_checkEmpty,
	_checkValidEmail,
	useCodeSentCountdown
} from 'containers/ac-containers/login/login';
import { SendSmsBtn } from 'containers/ac-containers/reset-password';
import {
	signUpWithSms,
	SignUpParams,
	emailCodeSent,
	checkEmailRegistered,
	EmailRegistered,
	Response
} from '@services/login';
import { emailCreateAccount } from './trace';
import { toast } from 'components/dialogs';


type signupMode = 'Phone' | 'Email'
type ChangeView = (view: View, token?: Token) => void
interface SignUpProps {
	className?: string;
	changeView: ChangeView;
}

function _getSignUpUrlQueryParam(): { channel_code: string; channel_keyword: string; inviteId: string } {
	const countryCode = getState().app.countryCode;
	const { channel_code, channel_keyword } = getChannelInfo(countryCode)
	const { inviteId = '' } = qsParse();
	return {
		channel_code,
		channel_keyword,
		inviteId
	}
}

type CheckAccount = (data: EmailRegistered, registeredCb?: (tip: string) => void) => Promise<Response<boolean>>
export const _checkAccount: CheckAccount = async (data, tipCb) => {
	try {
		const checkRes = await checkEmailRegistered(data)
		if (checkRes.data) typeof tipCb === 'function' && tipCb('The current email has been registered')
		return checkRes
	} catch (err) {
		console.log(err);
		throw err
	}
}

function InviteTip() {
	const { inviteId } = qsParse();
	return (
		!inviteId ? <></> : (<p className={styles.invite_tip}>Your friend invites you to join AirClass, register now and get a free trial lesson</p>)
	)
}

function PhoneMode(props: SignUpProps) {
	const { className, changeView } = props;
	const [area, setArea] = useState<Area>();
	const [areaTip, setAreaTip] = useState<string>('');
	const [phone, setPhone] = useState<string>('');
	const [phoneTip, setPhoneTip] = useState<string>('');
	const [next, setNext] = useState<boolean>(false);
	const [sms, setSms] = useState<string>('');
	const [smsTip, setSmsTip] = useState<string>('');
	const [alertTip, setAlertTip] = useState<string>('');
	const loadingRef = useRef<boolean>(false); // 是否登录中
	const { timerCount, timerCountRef, countdownHandler } = useCodeSentCountdown(120)
	const cardRef = useRef<HTMLDivElement | null>(null);
	const { transData } = useTranslation('login')
	const { channel_code, channel_keyword, inviteId } = _getSignUpUrlQueryParam()

	// 选择国家地区
	const onAreaChange = (item: any) => {
		setArea(item);
		setAreaTip('');
	}
	// 输入号码
	const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPhone(e.target.value);
		setPhoneTip('');
		setAlertTip('');
	}

	// 验证码登录
	const signUpWithSms = async () => {
		if (!area) return
		if (loadingRef.current) return; // 登录中
		loadingRef.current = true;
		try {
			const param = {
				areaCode: area.code,
				mobile: phone,
				validateCode: sms,
				key: channel_keyword,
				code: channel_code,
				referUserId: inviteId,
			}
			if (!inviteId) delete param.referUserId;
			const res = await Request.signUpWithSms(param);
			loadingRef.current = false;
			if (res.code === Request.ResponseCode.OK) {
				// 注册成功
				changeView(View.fill, res.data); // 完善个人信息 初次设置密码
			} else {
				// 注册失败
				setAlertTip(res.message);
			}
		} catch (err) {
			loadingRef.current = false;
		}
	};

	// 输入验证码
	const onSmsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSms(e.target.value);
		setSmsTip('');
		setAlertTip('');
	}

	// 发送验证码
	const sendSms = async () => {
		if (phoneTip || smsTip) return false;
		if (!area) {
			setAlertTip(transData.required);
			return false;
		}
		if (!phone) {
			setPhoneTip(transData.required);
			return false;
		}
		if (timerCountRef.current !== 0) return false;
		const send = await Request.getSignUpSmsCode({ areaCode: area.code, mobile: phone });
		if (send.data) {
			countdownHandler();
			return true;
		} else {
			setAlertTip(send.message);
			console.error('获取验证码失败')
			return false;
		}
	}

	// 验证码点击下一步
	const onSignUpHandle = () => {
		Trace.signupJoin();
		if (!sms.trim()) {
			setSmsTip(transData.required);
			return
		}
		signUpWithSms()
	}

	// get a code
	const onSmsNextHandle = async () => {
		Trace.signupGetTheCode();
		if (!area) {
			setAlertTip(transData.required);
			return
		}
		if (!phone) {
			setPhoneTip(transData.required);
			return
		}
		if (timerCountRef.current !== 0) return
		const send = await sendSms();
		if (send) {
			setAlertTip('');
			setNext(true);
		}
	}

	return (
		<div className={classNames(styles.signup, styles.card, className)} ref={cardRef}>
			<CardTitle title={transData.create} />
			<InviteTip />
			<AlertTip alertTip={alertTip} />
			<CardFormItem label={transData.phone} verifyTip={areaTip || phoneTip}>
				<Input
					className={styles.phone}
					placeholder={transData.phone}
					onChange={onPhoneChange}
					type='number'
					value={phone}
					prefix={(
						<AreaSelect
							selected={area}
							onChange={onAreaChange}
							width={cardRef.current?.offsetWidth}
						/>
					)}
				/>
			</CardFormItem>
			{
				next ? (
					<>
						<CardFormItem label={transData.code} verifyTip={smsTip}>
							<Input
								onChange={onSmsChange}
								placeholder={transData.code}
								value={sms}
								type='number'
								suffix={(
									<span onClick={sendSms}
										className={`${styles.send_sms_btn} ${timerCount === 0 ? styles.fire : ''}`}
									>{transData.sendText}{timerCount ? `${transData.in} ${timerCount}${transData.second}` : ''}</span>
								)}
							/>
						</CardFormItem>
						<Button onClick={onSignUpHandle}>
							{transData.join}
						</Button>
					</>
				) : (
					<Button disabled={!!(areaTip && phoneTip)} onClick={onSmsNextHandle}>{transData.codeText}</Button>
				)
			}
		</div >
	)
}

let _tempSuccessEmail = ''
function StepOne(props: { stepChange(): void }) {
	const { stepChange } = props
	const [alertTip, setAlertTip] = useState('')
	const [emailTip, setEmailTip] = useState('')
	const [email, setEmail] = useState('')
	const [codeGetLoading, setCodeGetLoading] = useState(false)

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value.trim())
		setEmailTip('')
		setAlertTip('')
	}

	const handleCodeGetClick = async () => {
		Trace.emailCreateAccount()
		if (_checkEmpty(email, setEmailTip)) return
		if (!_checkValidEmail(email, setEmailTip)) return
		try {
			setCodeGetLoading(true)
			const checkRes = await _checkAccount({ email, scene: 2 }, setEmailTip)
			setCodeGetLoading(false)
			if (checkRes?.data === true) return
			_tempSuccessEmail = email
			stepChange()
		} catch (e) {
			setCodeGetLoading(false)
		}
	}

	return (
		<CardWrap>
			<CardTitle title='Create Account' />
			<InviteTip />
			<AlertTip alertTip={alertTip} />
			<CardFormItem label='Email' verifyTip={emailTip}>
				<Input
					placeholder='Email'
					onChange={handleEmailChange}
					value={email}
				/>
			</CardFormItem>
			<Button disabled={!!emailTip} loading={codeGetLoading} onClick={handleCodeGetClick}>Get the Code</Button>
		</CardWrap>
	)
}
function StepTwo(props: { changeView: ChangeView }) {
	const { changeView } = props
	const [alertTip, setAlertTip] = useState('')
	const [codeSentTip, setCodeSentTip] = useState('')
	const [emailTip, setEmailTip] = useState('')
	const [email, setEmail] = useState('')
	const [code, setCode] = useState('')
	const [codeTip, setCodeTip] = useState('')
	const [codeSentLoading, setCodeSentLoading] = useState(false)
	const [submitLoading, setSubmitLoading] = useState(false)
	const { timerCount, timerCountRef, countdownHandler } = useCodeSentCountdown(120)
	const { channel_code, channel_keyword, inviteId } = _getSignUpUrlQueryParam()
	function _showCodeSentTip(email: string) {
		setCodeSentTip(`The authorization code has been sent to ${email}. Please check your email.`)
	}

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value.trim())
		setEmailTip('')
		setAlertTip('')
	}

	const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCode(e.target.value.trim())
		setCodeTip('')
		setAlertTip('')
	}

	const getCode = async (needCheck = true) => {
		const _email = email || _tempSuccessEmail
		try {
			setCodeSentLoading(true)
			if (needCheck) {
				const checkRes = await _checkAccount({ email: _email, scene: 2 }, setEmailTip)
				if (checkRes?.data === true) return
			}
			const sentRes = await emailCodeSent({ email: _email, scene: 2 })
			if (sentRes.data) {
				_showCodeSentTip(_email)
				countdownHandler() // 开启倒计时
			}
			_tempSuccessEmail = ''
		} finally {
			setCodeSentLoading(false)
		}
	}
	const handleCodeSentClick = () => {
		Trace.emailSignupResendCode()
		const emailCheckRes = !_checkEmpty(email, setEmailTip) && _checkValidEmail(email, setEmailTip)
		if (timerCountRef.current || !emailCheckRes || codeSentLoading) return
		getCode()
	}

	// 注册 join in
	const handleJoinClick = async () => {
		Trace.emailSignupJoin()
		const emailCheckRes = !_checkEmpty(email, setEmailTip) && _checkValidEmail(email, setEmailTip)
		const codeCheckRes = _checkEmpty(code, setCodeTip)
		if (!emailCheckRes || codeCheckRes) return
		const param: SignUpParams = {
			email,
			validateCode: code,
			key: channel_keyword,
			code: channel_code,
		}
		if (inviteId) param.referUserId = inviteId
		try {
			setSubmitLoading(true)
			const signupRes = await signUpWithSms({
				email,
				validateCode: code,
				key: channel_keyword,
				code: channel_code,
			})
			if (signupRes.data?.userId) {
				changeView(View.fill, signupRes.data); // 完善个人信息 初次设置密码
			} else {
				setSubmitLoading(false)
				setCodeSentTip('')
				setAlertTip(signupRes.message)
			}
		} catch (e) { // 如果在 finally 置为 false，changeView 导致组件在卸载时，还对 state 进行赋值，就会
			setSubmitLoading(false)
		}
	}

	useEffect(() => {
		setEmail(_tempSuccessEmail)
		if (!codeSentLoading) getCode(false)
	}, [])

	return (
		<CardWrap>
			<CardTitle title='Create Account' />
			<InviteTip />
			{
				!codeSentTip ?
					<AlertTip alertTip={alertTip}/> :
					<div className={styles.code_sent_tip_wrap}>
						<Alert type='success' className={styles.code_sent_tip} duration={5} onClose={() => setCodeSentTip('')}>
							<p>{codeSentTip}</p>
						</Alert>
					</div>
			}
			<CardFormItem label='Email' verifyTip={emailTip}>
				<Input
					placeholder='Email'
					onChange={handleEmailChange}
					// onBlur={handleEmailBlur}
					value={email}
				/>
			</CardFormItem>
			<CardFormItem label='Code' verifyTip={codeTip}>
				<Input
					type='number'
					placeholder='Code'
					onChange={handleCodeChange}
					value={code}
					suffix={<SendSmsBtn onClick={handleCodeSentClick} count={timerCount} />}
				/>
			</CardFormItem>
			<Button onClick={handleJoinClick} loading={submitLoading}>Join AirClass</Button>
		</CardWrap>
	)
}
function EmailMode(props: SignUpProps) {
	const [step, setStep] = useState(1)
	const handleStepChange = () => {
		setStep(step + 1)
	}
	return (
		<>
			{step === 1 && <StepOne stepChange={handleStepChange} />}
			{step === 2 && <StepTwo changeView={props.changeView} />}
		</>
	)
}

export default function SignUp(props: SignUpProps) {
	const { className, changeView } = props;
	const [signupMode, setSignupMode] = useState<signupMode>('Phone')
	const { transData } = useTranslation('login')

	const handleLoginModeChange = () => {
		setSignupMode(signupMode === 'Phone' ? 'Email' : 'Phone')
		signupMode === 'Phone' && Trace.changeToEmailCreateAccount()
	}

	return (
		<div className={styles.card_wrap}>
			{signupMode === 'Phone' && <PhoneMode className={className} changeView={changeView} />}
			{signupMode === 'Email' && <EmailMode changeView={changeView} />}
			<div className={styles.ua_pp}>
				{transData.descText}
				<Link to={generatePath(AGREEMENT_USER)}><span>{transData.descText1}</span></Link>
				{transData.descText2}
				<Link to={generatePath(AGREEMENT_POLICY)}><span>{transData.descText3}</span></Link>
			</div>
			<div className={styles.mode_switch_wrap}>
				<div className={styles.divide_line}>
					<span>OR</span>
				</div>
				<Button className={styles.switch_btn} type='primary' onClick={handleLoginModeChange}>
					<i className={classNames('iconfont', signupMode === 'Phone' ? 'icon_mail1' : 'icon_phone', styles.login_iconfont)} />
					{`Create with ${signupMode === 'Phone' ? 'Email' : 'Phone'}`}
				</Button>
			</div>
			<div className={styles.signup_footer}>
				<div className={styles.login_btn}>{transData.descText4}<span onClick={() => changeView(View.login)}>{transData.loginText}</span></div>
			</div>
		</div>
	)
}
